import React from "react";
import { Loader2, CheckCircle, MapPin, Building } from "lucide-react";
import { useGetAllinformationByid } from "../../adapters/Requests";
import PersonalDetailsCard from "../information_cards/PersonalDetailsCard";
import AcademicDetailsSiwes from "../information_cards/AcademicDetailsSiwes";
import AcademicDetailsIT from "../information_cards/AcademicDetailsIT";
import AcademicDetailsNDE from "../information_cards/AcademicDetailsNDE";
import DocumentUpload from "../information_cards/DocumentUpload";
import RefereeInformationCard from "../information_cards/RefereeInformationCard";
import CommentsCard from "../information_cards/CommentsCard";
import ApplicantPhoto from "../information_cards/ApplicantPhoto";

export default function AcceptedDetailsViewer({
  applicationId,
  internshipType,
}) {
  const { data, error, isLoading } = useGetAllinformationByid({
    application_id: applicationId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10 h-full">
        <Loader2 className="animate-spin text-green-600" size={32} />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error.message}</div>;
  }

  const vacancy = data?.vacancy?.id;
  const latestIntern = data?.Interns?.[0];
  const latestPlacement = latestIntern?.InternPlacements?.[0];
  const businessUnitName =
    latestPlacement?.business_unit_name || data?.vacancy?.business_unit?.name;
  const departmentName =
    data?.vacancy?.business_unit?.departments?.find(
      (department) => department.code === latestPlacement?.department_code,
    )?.name ||
    data?.vacancy?.business_unit?.departments?.[0]?.name ||
    latestPlacement?.department_code ||
    "N/A";
  const internId = latestIntern?.staff_id || data?.code || "N/A";
  let componentToRender;

  switch (internshipType?.toLowerCase()) {
    case "it":
      componentToRender = (
        <AcademicDetailsIT details={data?.ApplicationsAcademicQualification} />
      );
      break;
    case "siwes":
      componentToRender = (
        <AcademicDetailsSiwes
          details={data?.ApplicationsAcademicQualification}
        />
      );
      break;
    case "gap":
      componentToRender = (
        <AcademicDetailsNDE details={data?.ApplicationNYSCDetails} />
      );
      break;
    default:
      componentToRender = <></>;
  }

  return (
    <div className="space-y-6 pb-6 h-full overflow-y-auto px-1">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
        <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
          <CheckCircle size={16} className="text-slate-500" />
          <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Application Status
          </h2>
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <ApplicantPhoto applicationId={applicationId} />

            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded border border-green-200">
              <CheckCircle className="w-4 h-4" />
              <span className="font-bold text-xs uppercase tracking-wide">
                Accepted
              </span>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1 block">
                Placement Location
              </span>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span>
                  {data?.vacancy?.business_unit?.name},{" "}
                  {data?.vacancy?.location?.name}
                </span>
              </div>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1 block">
                Internship Type
              </span>
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <Building className="w-4 h-4 text-slate-400" />
                <span>
                  {data?.vacancy?.vacancy_position?.name?.toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1 block">
                Posted To
              </span>
              <div className="space-y-1 text-sm font-medium text-slate-600">
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-400" />
                  <span>{businessUnitName || "N/A"}</span>
                </div>
                <p className="pl-6 text-slate-500">{departmentName}</p>
              </div>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1 block">
                Intern ID
              </span>
              <div className="text-sm font-medium text-slate-600">
                <span className="text-slate-900">{internId}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {data?.Comments?.length > 0 && <CommentsCard comments={data?.Comments} />}

      <div className="flex flex-col gap-6">
        <PersonalDetailsCard
          applicant={data?.Applicant}
          nextOfKins={data?.ApplicationNextOfKins}
        />

        {componentToRender}

        <DocumentUpload
          documents={data?.ApplicationDocuments}
          vacancy={vacancy}
        />

        {!(internshipType?.toLowerCase() === "siwes") && (
          <RefereeInformationCard referees={data?.ApplicationReferees} />
        )}
      </div>
    </div>
  );
}
